import { useStyles } from "./styles";
import { Edit } from "@mui/icons-material";
import OrderItemCard from "./OrderItemCard";
import { useState, useMemo, useContext } from "react";
import styled from "@emotion/styled";
import {
    Chip,
    CircularProgress,
    LinearProgress,
    emphasize,
} from "@mui/material";
import reservedTableIcon from "./../../images/table-reserved-icon.svg";
import pencilIcon from "./../../images/Pincel.svg";
import _ from "lodash";
import { useRemoveItemsFromTableOrderData } from "../../shared/hooks/useTableOrderItemsData";
import {
    useAssignCustomerToTableData,
    useDeleteTableOrder,
    useTableOrders,
} from "../../shared/hooks/useTableOrdersData";
import Scrollbar from "../Scrollbar/Scrollbar";
import { useIsMutating } from "@tanstack/react-query";
import { AppContext } from "../../AppContext";
import ChooseCustomerDialog from "./ChooseCustomerDialog";
import moment from "moment";
import { getOrderShortName } from "../../utils/floorUtils";

const OrderWidget = ({
    handleSelectOrder,
    handleCancelOrder,
    handleCloseTable,
    handleCreateOrder,
    handleSaveToTable,
    table,
    items,
    selectedOrder,
    floor,
    minified = false,
    handleChangeCustomer,
    isItemsLoading,
}) => {
    const classes = useStyles();

    const [vat, setVat] = useState(0);
    const [subTotal, setSubTotal] = useState(0);
    const [total, setTotal] = useState(0);
    const [displayedCustomer, setDisplayedCustomer] = useState(0);

    const [expandedItem, setExpandedItem] = useState({});
    const [closeActionString, setCloseActionString] = useState("Close table");

    const { globalState, menuAddons } = useContext(AppContext);

    const {
        isLoading: isTableOrdersLoading,
        data: tableOrders,
        isError: isTableOrdersError,
        error: tableOrdersError,
    } = useTableOrders(table?.name || null);

    const [isLoading, setIsloading] = useState(true);
    const isMutating = useIsMutating();

    const removeOrderItemsMutation = useRemoveItemsFromTableOrderData();

    const deleteTableOrderMutation = useDeleteTableOrder();

    const [canSendToKitchen, setCanSendToKitchen] = useState(true);

    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);

    const [optimisticCustomer, setOptimisticCustomer] = useState("");
    //
    const assignTableMutation = useAssignCustomerToTableData();

    useMemo(() => {
        setIsloading(isItemsLoading);
    }, [isItemsLoading]);

    useMemo(() => {
        if (isMutating == 1) {
            setIsloading(true);
        } else {
            setIsloading(false);
        }
    }, [isMutating]);

    const onSave = async () => {
        // Call the provided onClick function when the component is clicked
        if (isLoading) {
            await new Promise((resolve) => {
                const checkLoading = setInterval(() => {
                    if (!isLoading) {
                        clearInterval(checkLoading);
                        resolve();
                    }
                }, 100);
            });
        }
        // Only after we finished loading we will just forward...
        if (handleSaveToTable) {
            handleSaveToTable(table);
        }
    };

    // Weather or not we have to send to kitchen.
    const checkCanSendToKitchen = () => {
        if (!items || items.length == 0) return false;

        for (const item of items) {
            if (item.status === "Attending") {
                return true;
            }
        }

        return false;
    };

    const checkCanCloseTable = () => {
        if (!items || items.length == 0) return false;

        for (const item of items) {
            if (item.status !== "Attending") {
                return true;
            }
        }

        return false;
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    const onConfirmCustomerChanged = (customerName) => {
        const date = new Date();

        const formattedDate = moment(date).format(
            "YYYY-MM-DD HH:mm:ss.SSSSSSSSS"
        );
        let payload = {
            name: selectedOrder.name,
            owner: globalState.loggedInUser,
            creation: formattedDate,
            modified: formattedDate,
            table: table.name,
            doctype: "Table Order",
            customer: customerName,
        };

        assignTableMutation.mutate(
            {
                orderId: selectedOrder.name,
                payload: payload,
            },
            {
                onSuccess: (data) => {
                    setOptimisticCustomer(customerName);
                    // we will use optimistic update because we know it has changed.

                    setChangeCustomerDialogOpen(false);
                },
            }
        );
    };

    const onTableClose = () => {
        if (items && items.length > 0) {
            if (handleCloseTable) handleCloseTable(selectedOrder, table);
        } else {
            if (handleCancelOrder) {
                handleCancelOrder();
            }
        }
    };

    const onSelectOrder = (order) => {
        if (handleSelectOrder) {
            handleSelectOrder(order);
        }
    };

    const cancelOrder = () => {
        if (isTableOrdersLoading) return;
        //check the table orders.
        let totalItemsCount = 0;
        let ordersNames = [];
        let elementsToRemove = [];
        if (tableOrders && tableOrders.length > 0) {
            tableOrders.forEach((order) => {
                const { items_count } = order.data;
                ordersNames.push(order.name);
                totalItemsCount += items_count;
            });
        }

        if (items && items.length > 0) {
            items.forEach((item) => {
                const { status } = item;
                if (status === "Attending") {
                    elementsToRemove.push(item);
                }
            });
        }

        if (elementsToRemove.length > 0) {
            removeOrderItemsMutation.mutate(
                {
                    items: elementsToRemove,
                },
                {
                    onSettled: () => {
                        deleteEmptyOrder(totalItemsCount, ordersNames);
                    },
                }
            );
        } else {
            deleteEmptyOrder(totalItemsCount, ordersNames);
        }
    };

    const deleteEmptyOrder = (totalItemsCount, ordersNames) => {
        if (totalItemsCount === 0 && ordersNames.length > 0) {
            deleteTableOrderMutation.mutate(
                {
                    tableId: table?.name,
                    orderName: ordersNames[0],
                },
                {
                    onSettled: (data) => {
                        // Delete finished weather true or false.
                        if (handleCancelOrder) {
                            handleCancelOrder();
                        }
                    },
                }
            );
        } else {
            if (handleCancelOrder) {
                handleCancelOrder();
            }
        }
    };

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    useMemo(() => {
        let addonsTotal = 0;
        if (items && items.length > 0) {
            const { total, count } = items.reduce(
                (acc, curr) => {
                    acc.count += curr.qty;
                    acc.total += curr.qty * curr.rate;
                    return acc;
                },
                { total: 0, count: 0 }
            );

            items.forEach((item) => {
                if (item.addons) {
                    const intersection = _.intersectionWith(
                        menuAddons,
                        JSON.parse(item.addons),
                        (item1, item2) => item1.item_code === item2
                    );

                    if (intersection && intersection.length > 0) {
                        const intersectionTotal = _.sumBy(
                            intersection,
                            "standard_rate"
                        );
                        addonsTotal += intersectionTotal;
                    }
                }
            });
            setTotal(total + addonsTotal);
        } else {
            setTotal(0);
        }

        let canCloseTable = checkCanCloseTable();
        if (canCloseTable) {
            setCloseActionString("Pay-Close order");
        } else {
            setCloseActionString("Delete order");
        }

        setCanSendToKitchen(checkCanSendToKitchen());
    }, [items]);

    useMemo(() => {
        if (tableOrders && tableOrders.length > 0) {
            setDisplayedCustomer(tableOrders[0].data.customer);
        } else {
            setDisplayedCustomer("");
        }
    }, [tableOrders]);

    useMemo(() => {
        if (optimisticCustomer && optimisticCustomer.length > 0) {
            setDisplayedCustomer(optimisticCustomer);
        }
    }, [optimisticCustomer]);

    return (
        <>
            <div className={classes.OrderWidget}>
                <div>
                    <div className={classes.TableDetails}>
                        {table.reserved ? (
                            <div className={classes.Table_ReservedWrap}>
                                <div>
                                    <img src={reservedTableIcon} />
                                </div>
                            </div>
                        ) : (
                            <div className="TableIcon"></div>
                        )}
                        <div className="TableName">
                            <div>Table details</div>
                            <div>
                                {floor?.description}, {table.tableDescription}
                            </div>
                        </div>
                        <div className="Actions">
                            {!isLoading ? (
                                <i
                                    className="bx bxs-trash"
                                    onClick={() => cancelOrder()}
                                ></i>
                            ) : (
                                <i></i>
                            )}
                        </div>
                    </div>
                    <div className={classes.CustomerDetails}>
                        <div className="CustomerName">
                            <div>{displayedCustomer}</div>
                        </div>
                        <div className={classes.Table_edit_customer}>
                            <div
                                onClick={() =>
                                    changeCustomer(displayedCustomer)
                                }
                            >
                                <img src={pencilIcon} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={classes.OrdersNav}>
                    <Scrollbar>
                        {tableOrders && tableOrders.length > 0 ? (
                            tableOrders.map((order) => (
                                <StyledBreadcrumb
                                    key={`${order.name}`}
                                    component="a"
                                    label={getOrderShortName(order.name)}
                                    icon={
                                        selectedOrder &&
                                        order.name === selectedOrder.name ? (
                                            <Edit />
                                        ) : null
                                    }
                                    clickable
                                    onClick={() => onSelectOrder(order)}
                                />
                            ))
                        ) : (
                            <StyledBreadcrumb
                                component="a"
                                icon={
                                    <CircularProgress className="CircularProgress" />
                                }
                            />
                        )}
                    </Scrollbar>
                </div>

                <div className={classes.OrderItems}>
                    <LinearProgress
                        className={classes.OrderItemsProgress}
                        style={{ display: isLoading ? "block" : "none" }}
                    ></LinearProgress>
                    <Scrollbar>
                        {items &&
                            items.map((item, index) => (
                                <OrderItemCard
                                    key={
                                        "OrderItem_" +
                                            index +
                                            "_" +
                                            item.item.identifier ||
                                        item.entry_name
                                    }
                                    clickable
                                    item={item}
                                    expanded={expandedItem}
                                    onChange={() =>
                                        expandedItem == item.entry_name
                                            ? setExpandedItem(false)
                                            : setExpandedItem(item.entry_name)
                                    }
                                    order={selectedOrder}
                                />
                            ))}{" "}
                    </Scrollbar>
                </div>

                <div className={classes.Pricing}>
                    <div className="Pricing__Components">
                        <div>
                            <span>VAT</span>
                            <span>{vat}</span>
                        </div>
                        <div>
                            <span>Subtotal</span>
                            <span>{subTotal}</span>
                        </div>
                    </div>
                    <div className="Pricing__Total">
                        <div>
                            <span>Total</span>
                            <span>{total}</span>
                        </div>
                    </div>
                </div>
                {minified ? (
                    !table?.reserved ? (
                        <div className={classes.Actions}>
                            <button
                                className="Btn--Primary"
                                onClick={onTableClose}
                            >
                                close table
                            </button>
                        </div>
                    ) : null
                ) : (
                    <div className={classes.Actions}>
                        {canSendToKitchen ? (
                            <button className="Btn--Primary" onClick={onSave}>
                                Send to kitchen
                            </button>
                        ) : (
                            <div></div>
                        )}
                        {/*<button className='Btn--Secondary' onClick={onCreateOrder}>New order</button>*/}
                        {!table?.reserved ? (
                            <button
                                className="Btn--Primary"
                                onClick={onTableClose}
                            >
                                {closeActionString}
                            </button>
                        ) : null}
                    </div>
                )}

                <ChooseCustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                ></ChooseCustomerDialog>
            </div>
        </>
    );
};

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === "light"
            ? "#EFEFEF" // Custom light color
            : "#333333"; // Custom dark color
    return {
        backgroundColor,
        // color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        "&:hover, &:focus": {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        "&:active": {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
        "& .CircularProgress": {
            width: "15px !important",
            height: "15px !important",
            color: "#e14954",
        },
        padding: "5px 12px",
        background: "#6E7187",
        borderRadius: "11.25px",
        fontSize: "8px",
        lineHeight: "10px",
        textAlign: "center",
        textTransform: "uppercase",
        color: "#FFFFFF",
    };
});

export default OrderWidget;
